import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';

export type AppTheme = 'light' | 'dark';

const STORAGE_KEY = 'app-theme';

/**
 * Single source of truth for light/dark theming.
 * Applies `data-theme` (drives our --app-* tokens) and `data-coreui-theme`
 * (drives CoreUI components) on the <html> element, and persists the choice.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  /** Reactive current theme; components can read `theme()` in templates. */
  readonly theme = signal<AppTheme>('light');

  /** Call once at app start to restore the saved (or system) theme. */
  init(): void {
    this.apply(this.resolveInitialTheme());
  }

  toggle(): void {
    this.apply(this.theme() === 'dark' ? 'light' : 'dark');
  }

  set(theme: AppTheme): void {
    this.apply(theme);
  }

  private apply(theme: AppTheme): void {
    const root = this.document.documentElement;
    root.setAttribute('data-theme', theme);
    root.setAttribute('data-coreui-theme', theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* storage may be unavailable (private mode) — non-fatal */
    }
    this.theme.set(theme);
  }

  private resolveInitialTheme(): AppTheme {
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    if (saved === 'light' || saved === 'dark') {
      return saved;
    }
    const prefersDark =
      this.document.defaultView?.matchMedia?.(
        '(prefers-color-scheme: dark)',
      ).matches ?? false;
    return prefersDark ? 'dark' : 'light';
  }
}
